import { planLabel } from '../lib/plans'
import { pb, escapeFilterValue } from '../lib/pocketbase'
import { SUPPORT_EMAIL, NOREPLY_EMAIL } from '../lib/config'

// PendingSubscription - matches PocketBase pending_subscriptions collection schema
export type PendingSubscription = {
  id: string
  email: string | null
  plan: string | null // Common values: 'starter', 'pro', 'free'
  stripe_session_id: string | null
  stripe_customer_id: string | null
  status: string | null // Common values: 'pending', 'claimed', 'expired', 'invited'
  agency_id: string | null // Pre-assigned agency for this customer
  agency_name: string | null // Name of the agency (for display)
  requested_agency_name: string | null // Customer's requested agency name from payment success page
  role: string | null // Role to assign when user registers: 'admin', 'agent', 'assistant', 'intern'
  created: string
  updated: string
}

/**
 * Get all pending subscriptions (super admin only)
 */
export async function getAllPendingSubscriptions(): Promise<PendingSubscription[]> {
  
  const records = await pb.collection('pending_subscriptions').getFullList<PendingSubscription>({
    sort: '-created'
  })
  return records
}

/**
 * Update pending subscription with agency assignment and role
 */
export async function assignAgencyToPendingSubscription(
  subscriptionId: string,
  agencyId: string,
  agencyName: string,
  role?: string
): Promise<void> {
  try {
    const updateData: Record<string, any> = {
      agency_id: agencyId,
      agency_name: agencyName
    }
    if (role) {
      updateData.role = role
    }
    await pb.collection('pending_subscriptions').update(subscriptionId, updateData)
  } catch (error: any) {
    throw new Error(`Failed to assign agency to subscription ${subscriptionId}: ${error.message}`)
  }
}

/**
 * Update role for a pending subscription
 */
export async function updatePendingSubscriptionRole(
  subscriptionId: string,
  role: string
): Promise<void> {
  try {
    await pb.collection('pending_subscriptions').update(subscriptionId, {
      role: role
    })
  } catch (error: any) {
    throw new Error(`Failed to update role for subscription ${subscriptionId}: ${error.message}`)
  }
}

/**
 * Mark pending subscription as invited (email sent)
 */
export async function markAsInvited(subscriptionId: string): Promise<void> {
  try {
    await pb.collection('pending_subscriptions').update(subscriptionId, {
      status: 'invited'
    })
  } catch (error: any) {
    throw new Error(`Failed to mark subscription ${subscriptionId} as invited: ${error.message}`)
  }
}

/**
 * Save requested agency name from payment success page
 */
export async function saveRequestedAgencyName(
  sessionId: string,
  requestedName: string
): Promise<void> {
  try {
    // Find the pending subscription by session ID
    const records = await pb.collection('pending_subscriptions').getList<PendingSubscription>(1, 1, {
      filter: `stripe_session_id = "${escapeFilterValue(sessionId)}"`
    })

    if (records.items.length === 0) {
      throw new Error(`No pending subscription found for session: ${sessionId}`)
    }

    // Update with requested agency name
    await pb.collection('pending_subscriptions').update(records.items[0].id, {
      requested_agency_name: requestedName.trim()
    })
  } catch (error: any) {
    throw new Error(`Failed to save requested agency name: ${error.message}`)
  }
}

/**
 * Generate invite URL for a pending subscription
 */
export function generateInviteUrl(sessionId: string, email: string): string {
  const baseUrl = import.meta.env.VITE_APP_URL || 'https://www.reppr.io'
  return `${baseUrl}/register?session_id=${sessionId}&email=${encodeURIComponent(email)}`
}

/**
 * Send invite email to customer
 */
export async function sendInviteEmail(
  customerEmail: string,
  plan: string,
  sessionId: string,
  agencyName?: string
): Promise<void> {
  try {
    const baseUrl = import.meta.env.VITE_APP_URL || 'https://www.reppr.io'
    const registerUrl = `${baseUrl}/register?session_id=${sessionId}&email=${encodeURIComponent(customerEmail)}`

    // Shared label map — this path and the admin notification in
    // api/stripe-webhook.js used to disagree, so the same subscriber was
    // called "Agency" here and "Pro" there.
    const planName = planLabel(plan)

    const agencyText = agencyName
      ? `Your agency <strong>${agencyName}</strong> has been set up and is ready for you.`
      : 'Your account is ready to be set up.'

    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${pb.authStore.token}`,
      },
      body: JSON.stringify({
        to: customerEmail,
        subject: 'Complete Your Reppr Registration',
        body: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #C8A45C 0%, #7A6430 100%); color: white; padding: 30px; border-radius: 0; text-align: center; }
    .content { background: #f8f9fa; padding: 30px; border: 1px solid #e9ecef; }
    .footer { background: #f1f3f5; padding: 20px; border-radius: 0; font-size: 12px; color: #666; text-align: center; }
    .button { display: inline-block; background: #C8A45C; color: white; padding: 14px 28px; border-radius: 0; text-decoration: none; font-weight: 600; margin: 20px 0; }
    .success-badge { display: inline-block; background: #dcfce7; color: #166534; padding: 6px 12px; border-radius: 0; font-size: 14px; font-weight: 500; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0 0 10px;">Welcome to Reppr!</h1>
      <span class="success-badge">${planName} Plan</span>
    </div>
    <div class="content">
      <p>Hi there,</p>
      <p>Thank you for subscribing to Reppr! ${agencyText}</p>
      <p><strong>Click the button below to complete your registration:</strong></p>
      <div style="text-align: center;">
        <a href="${registerUrl}" class="button">Complete Registration</a>
      </div>
      <p style="color: #666; font-size: 14px;">This link will expire in 24 hours. If you have any issues, please contact support.</p>
    </div>
    <div class="footer">
      <p>Reppr - AI-Powered Sports Agency Management</p>
      <p>Questions? Contact us at ${SUPPORT_EMAIL}</p>
    </div>
  </div>
</body>
</html>`,
        from: NOREPLY_EMAIL,
        fromName: 'Reppr'
      })
    })

    if (!response.ok) {
      throw new Error(`Invite email API returned ${response.status}`)
    }
  } catch (error: any) {
    throw new Error(`Failed to send invite email to ${customerEmail}: ${error.message}`)
  }
}

/**
 * Check if there's a pending subscription for the given email
 * Called during registration to verify payment
 */
export async function checkPendingSubscription(email: string): Promise<PendingSubscription | null> {
  
  const normalizedEmail = email.toLowerCase().trim()

  // Find pending subscription matching this email
  const records = await pb.collection('pending_subscriptions').getList<PendingSubscription>(1, 1, {
    filter: `email = "${escapeFilterValue(normalizedEmail)}" && status = "pending"`,
    sort: '-created'
  })

  if (records.items.length > 0) {
    return records.items[0]
  }

  return null
}

/**
 * Mark a pending subscription as claimed after user registration
 */
export async function claimPendingSubscription(subscriptionId: string): Promise<void> {
  try {
    await pb.collection('pending_subscriptions').update(subscriptionId, {
      status: 'claimed'
    })
  } catch (error: any) {
    throw new Error(`Failed to claim subscription ${subscriptionId}: ${error.message}`)
  }
}

/**
 * Update user's plan after verifying payment
 */
export async function updateUserPlan(
  userId: string,
  plan: string,
  stripeCustomerId?: string
): Promise<void> {
  try {
    const updateData: Record<string, any> = {
      plan: plan,
      subscription_status: 'active'
    }

    if (stripeCustomerId) {
      updateData.stripe_customer_id = stripeCustomerId
    }

    await pb.collection('users').update(userId, updateData)
  } catch (error: any) {
    throw new Error(`Failed to update user plan for ${userId}: ${error.message}`)
  }
}

/**
 * Validate pending subscription for registration access
 * Returns validated subscription data if payment is valid, null otherwise
 * Used to gate access to the registration page
 */
export async function validatePendingSubscriptionForRegistration(
  sessionId?: string | null,
  email?: string | null
): Promise<{ valid: boolean; email: string | null; plan: string; subscriptionId: string; agencyId?: string; agencyName?: string } | null> {
  
  // Must have either session_id or email to validate
  if (!sessionId && !email) {
    return null
  }

  let filter = ''
  if (sessionId) {
    // Validate by Stripe session ID (most secure)
    // Allow both 'pending' and 'invited' status
    filter = `stripe_session_id = "${escapeFilterValue(sessionId)}" && (status = "pending" || status = "invited")`
  } else if (email) {
    // Fallback to email validation
    const normalizedEmail = email.toLowerCase().trim()
    filter = `email = "${escapeFilterValue(normalizedEmail)}" && (status = "pending" || status = "invited")`
  }

  const records = await pb.collection('pending_subscriptions').getList<PendingSubscription>(1, 1, {
    filter,
    sort: '-created'
  })

  if (records.items.length === 0) {
    return null
  }

  const pending = records.items[0]

  // Check if subscription is not too old (24 hours max)
  const createdAt = new Date(pending.created)
  const now = new Date()
  const hoursDiff = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60)

  if (hoursDiff > 168) { // 7 days
    // Mark as expired and return null
    await pb.collection('pending_subscriptions').update(pending.id, {
      status: 'expired'
    })
    return null
  }

  return {
    valid: true,
    email: pending.email ?? null,
    plan: pending.plan || 'starter',
    subscriptionId: pending.id,
    agencyId: pending.agency_id || undefined,
    agencyName: pending.agency_name || undefined
  }
}

/**
 * Complete flow: Check for pending subscription and apply it to user
 * Called after successful registration
 * Returns agencyId if a pre-assigned agency exists
 */
/**
 * Claim the subscription a payment created, and attach this account to it.
 *
 * This used to do the work in the browser, and the important half could not
 * succeed: `users.updateRule` guards `role` and `agency_id`, so
 * `users.update({ agency_id, role })` always 403'd. A paying customer saw
 * "subscription linking failed", was routed to onboarding, and had no way
 * through — onboarding takes an invitation code, which a payer never gets.
 *
 * The endpoint reads the email off the verified token rather than trusting one
 * from here, so this function deliberately no longer passes one. It does pass
 * the Stripe checkout session from the payer's registration link, when there
 * is one: with an unverified email, that is the proof the account is theirs.
 */
export async function applyPendingSubscription(
  _userId: string,
  _email: string,
  sessionId?: string | null,
): Promise<{ success: boolean; plan?: string; agencyId?: string }> {
  const res = await fetch('/api/claim-subscription', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${pb.authStore.token}`,
    },
    body: JSON.stringify(sessionId ? { session_id: sessionId } : {}),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok && res.status !== 207) {
    throw new Error(data.error || `Could not link your subscription (${res.status}).`)
  }
  // 207 means the account is set up but the payment row was not closed out.
  // The caller gets a working account either way, which is what it asks about.
  return { success: true, plan: data.plan, agencyId: data.agencyId }
}
